import { useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { formatINR } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { ErrorBanner } from '../components/ui';

export function SettingsPage() {
  const { settings, reloadSettings } = useStore();
  const [rate, setRate] = useState(String(settings.default_rate_inr));
  const [weekStart, setWeekStart] = useState(settings.week_starts_on);
  const [ownerName, setOwnerName] = useState(settings.owner_name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!Number.isFinite(Number(rate)) || Number(rate) < 0) {
      setError('Default rate must be a non-negative number');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.saveSettings({
        default_rate_inr: Number(rate),
        week_starts_on: weekStart,
        owner_name: ownerName.trim() || null,
      });
      await reloadSettings();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save settings');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Defaults applied across the whole timesheet." />

      <div className="card max-w-xl px-6 py-6">
        {error && (
          <div className="mb-4">
            <ErrorBanner message={error} onDismiss={() => setError(null)} />
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label className="label" htmlFor="s-name">
              Your name
            </label>
            <input
              id="s-name"
              className="field"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Shown in the header"
            />
          </div>

          <div>
            <label className="label" htmlFor="s-rate">
              Default rate per hour (INR)
            </label>
            <input
              id="s-rate"
              className="field nums"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            <p className="mt-1.5 text-[12px] text-[var(--color-ink-muted)]">
              Used by any project without its own rate. Currently {formatINR(settings.default_rate_inr)} per hour.
            </p>
          </div>

          <div>
            <span className="label">Week starts on</span>
            <div className="inline-flex rounded-[var(--radius-pill)] border border-[var(--color-line)] bg-white p-1">
              {[
                { value: 1, label: 'Monday' },
                { value: 0, label: 'Sunday' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setWeekStart(option.value)}
                  className={`rounded-[var(--radius-pill)] px-4 py-1.5 text-[13px] font-semibold ${
                    weekStart === option.value
                      ? 'bg-[var(--color-blue-600)] text-white'
                      : 'text-[var(--color-ink-soft)]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 border-t border-[var(--color-line)] pt-5">
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
          {saved && <span className="text-[13px] text-[var(--color-cyan-600)]">Saved.</span>}
        </div>
      </div>

      <div className="card mt-5 max-w-xl px-6 py-5">
        <h2 className="text-[16px]">Changing your password</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-ink-soft)]">
          The sign-in password is a Cloudflare secret, not a database row. To change it, generate a
          new hash and upload it:
        </p>
        <pre className="mt-3 overflow-x-auto rounded-xl bg-[var(--color-paper-sunk)] px-4 py-3 text-[12px] leading-relaxed">
          <code>{`node scripts/hash-password.mjs 'new password'
npx wrangler pages secret put OWNER_PASSWORD_HASH`}</code>
        </pre>
      </div>
    </>
  );
}
